import {
  allSalesInvoices,
  type SalesInvoice,
  type InvoiceLineItem,
  type InvoiceStatus,
  type InvoiceTemplate,
  type SettlementKind,
} from 'helpers/fakeData';

export interface StatusMeta {
  label: string;
  palette: string;
}

export const INVOICE_STATUS_META: Record<InvoiceStatus, StatusMeta> = {
  draft: { label: 'Draft', palette: 'gray' },
  sent: { label: 'Sent', palette: 'blue' },
  partially_paid: { label: 'Partially paid', palette: 'yellow' },
  paid: { label: 'Paid', palette: 'green' },
  void: { label: 'Void', palette: 'red' },
};

export const INVOICE_TEMPLATE_META: Record<InvoiceTemplate, { label: string; description: string }> = {
  standard: { label: 'Standard', description: 'Logo, classic layout' },
  spreadsheet: { label: 'Spreadsheet', description: 'Gridded, tax-invoice style' },
  continental: { label: 'Continental', description: 'Bold header, balance up top' },
  compact: { label: 'Compact', description: 'Tight, details stacked right' },
};

export const SETTLEMENT_META: Record<SettlementKind, { label: string; description: string }> = {
  online: {
    label: 'Online',
    description: 'Collect through our payment infrastructure — recorded automatically.',
  },
  cash: {
    label: 'Cash / offline',
    description: "Record received payments manually.",
  },
};

export const lineAmount = (item: InvoiceLineItem): number => {
  return item.quantity * item.rate;
};

export const invoiceSubtotal = (invoice: SalesInvoice): number => {
  return invoice.lineItems.reduce((sum, item) => sum + lineAmount(item), 0);
};

export const invoiceDiscountAmount = (invoice: SalesInvoice): number => {
  return invoiceSubtotal(invoice) * (invoice.discountPercent / 100);
};

export const invoiceTaxAmount = (invoice: SalesInvoice): number => {
  return invoice.lineItems.reduce(
    (sum, item) => sum + lineAmount(item) * ((item.taxRatePercent ?? 0) / 100),
    0,
  );
};

export const invoiceTotal = (invoice: SalesInvoice): number => {
  return (
    invoiceSubtotal(invoice) -
    invoiceDiscountAmount(invoice) +
    invoiceTaxAmount(invoice) +
    invoice.shippingCharge +
    invoice.adjustment
  );
};

export const invoiceBalanceDue = (invoice: SalesInvoice): number => {
  // Round to minor units so float drift can't leave a phantom sub-cent balance.
  return Math.max(Math.round((invoiceTotal(invoice) - invoice.receivedAmount) * 100) / 100, 0);
};

// A finalized invoice is locked — no edits, only share / collect / void.
export const isFinalized = (invoice: SalesInvoice): boolean => {
  return invoice.status !== 'draft';
};

export const formatMoney = (amount: number): string => {
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Parse a 'YYYY-MM-DD' string into numeric parts (null if malformed). All date
// helpers work in UTC so results never shift with the viewer's timezone.
const parseIso = (iso: string): { y: number; m: number; d: number } | null => {
  const [y, m, d] = iso.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return null;
  }
  return { y, m, d };
};

export const formatDate = (iso: string): string => {
  if (iso === '') {
    return '—';
  }
  const parts = parseIso(iso);
  if (parts == null) {
    return iso;
  }
  const date = new Date(Date.UTC(parts.y, parts.m - 1, parts.d));
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

// Returns a YYYY-MM-DD string `days` after the given ISO date.
export const addDays = (iso: string, days: number): string => {
  const parts = parseIso(iso);
  if (parts == null) {
    return iso;
  }
  const date = new Date(Date.UTC(parts.y, parts.m - 1, parts.d));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

export const endOfMonth = (iso: string): string => {
  const parts = parseIso(iso);
  if (parts == null) {
    return iso;
  }
  // Day 0 of the next month is the last day of this month.
  const date = new Date(Date.UTC(parts.y, parts.m, 0));
  return date.toISOString().slice(0, 10);
};

export const invoicesForCustomer = (customerId: string): SalesInvoice[] => {
  return allSalesInvoices.filter((invoice) => invoice.customerId === customerId);
};

// Outstanding balance across a customer's non-void invoices.
export const customerBalanceDue = (customerId: string): number => {
  return invoicesForCustomer(customerId)
    .filter((invoice) => invoice.status !== 'void')
    .reduce((sum, invoice) => sum + invoiceBalanceDue(invoice), 0);
};
